// src/components/LoadDialogContent.tsx
/**
 * T-35: Load Dialog Content Component
 * Content component for the new UI framework Load Dialog
 * Extracted from the existing LoadDialog component
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useDialog } from './ui';
import { setShareLoading, setLoadedFromShare } from '../store/slices/shareSlice';
import { loadSharedScene, extractSceneIdFromUrl } from '../lib/shareApi';
import { loadViewportState } from '../store/slices/viewportSlice';
import { loadNumberLineState } from '../store/slices/numberLinesSlice';
import { loadSecondaryLineState } from '../store/slices/secondaryLineSlice';
import { loadExpressionsState } from '../store/slices/expressionsSlice';
import { loadJumpIndicatorsState } from '../store/slices/jumpIndicatorsSlice';
import { loadAnnotationsState } from '../store/slices/annotationsSlice';
import { loadConstantsState } from '../store/slices/constantsSlice';
import { updatePreferences } from '../store/slices/preferencesSlice';
import { loadLabelVisibilityState } from '../store/slices/labelVisibilitySlice';
import { loadCustomTicksState } from '../store/slices/customTicksSlice';
import { loadMeasurementsState } from '../store/slices/uiSlice';
import { setMultiplierMarkers } from '../store/slices/multiplierSlice';
import { loadEquivalentFractionsState } from '../store/slices/equivalentFractionsSlice';
import { loadDNLToolsState } from '../store/slices/dnlToolsSlice';

interface LoadResult {
  state: any;
  metadata: {
    id: string;
    size_bytes: number;
    schema_version: number;
    created_at: string;
    access_count: number;
  };
}

export const LoadDialogContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { closeDialog } = useDialog();
  const isLoading = useAppSelector(state => state.share.isLoading);
  const loadedFromShare = useAppSelector(state => state.share.loadedFromShare);
  
  const [input, setInput] = useState('');
  const [sceneId, setSceneId] = useState('');
  const [loadResult, setLoadResult] = useState<LoadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset state when component mounts
  useEffect(() => {
    setInput('');
    setSceneId('');
    setLoadResult(null);
    setError(null);
    setShowPreview(false);
    
    // Check if there are unsaved changes (simplified check)
    setHasUnsavedChanges(!loadedFromShare);
  }, [loadedFromShare]);

  // Extract scene ID from input (URL or direct ID)
  useEffect(() => {
    if (input.trim()) {
      // Try to extract from URL first
      const extractedId = extractSceneIdFromUrl(input.trim());
      if (extractedId) {
        setSceneId(extractedId);
        setError(null);
      } else {
        // Check if it's a direct scene ID (12-character hex)
        const directId = input.trim();
        if (/^[a-f0-9]{12}$/i.test(directId)) {
          setSceneId(directId.toLowerCase());
          setError(null);
        } else {
          setSceneId('');
          if (input.length > 0) {
            setError('Please enter a valid scene ID (12 characters) or share URL');
          }
        }
      }
    } else {
      setSceneId('');
      setError(null);
    }
  }, [input]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    setLoadResult(null);
    setShowPreview(false);
  }, []);

  const handlePreview = useCallback(async () => {
    if (!sceneId) return;

    setError(null);
    dispatch(setShareLoading(true));
    
    try {
      console.log('Loading scene preview for ID:', sceneId);
      const result = await loadSharedScene(sceneId);
      setLoadResult(result);
      setShowPreview(true);
      
      console.log('Preview loaded successfully:', result.metadata);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load scene';
      console.error('Preview failed:', err);
      setError(errorMessage);
    } finally {
      dispatch(setShareLoading(false));
    }
  }, [sceneId, dispatch]);

  const handleLoadScene = useCallback(() => {
    if (!loadResult) return;

    console.log('Loading scene state into Redux store:', loadResult.state);
    const state = loadResult.state;
    
    // Load all state slices from the shared scene
    if (state.viewport) {
      dispatch(loadViewportState(state.viewport));
    }
    if (state.numberLines) {
      dispatch(loadNumberLineState(state.numberLines));
    }
    if (state.secondaryLine) {
      dispatch(loadSecondaryLineState(state.secondaryLine));
    }
    if (state.expressions) {
      dispatch(loadExpressionsState(state.expressions));
    }
    if (state.jumpIndicators) {
      dispatch(loadJumpIndicatorsState(state.jumpIndicators));
    }
    if (state.multipliers) {
      dispatch(setMultiplierMarkers(state.multipliers));
    }
    if (state.annotations) {
      dispatch(loadAnnotationsState(state.annotations));
    }
    if (state.constants) {
      dispatch(loadConstantsState(state.constants));
    }
    if (state.preferences) {
      dispatch(updatePreferences(state.preferences));
    }
    if (state.labelVisibility) {
      dispatch(loadLabelVisibilityState(state.labelVisibility));
    }
    if (state.customTicks) {
      dispatch(loadCustomTicksState(state.customTicks));
    }
    if (state.dnlTools) {
      dispatch(loadDNLToolsState(state.dnlTools));
    }
    if (state.measurements) {
      dispatch(loadMeasurementsState(state.measurements));
    }
    if (state.equivalentFractions) {
      dispatch(loadEquivalentFractionsState(state.equivalentFractions));
    }

    // Mark as loaded from share
    dispatch(setLoadedFromShare(true));

    console.log('Scene loaded successfully');
    closeDialog();
  }, [loadResult, dispatch]);

  const handlePasteFromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setInput(clipboardText);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      setError('Failed to read from clipboard');
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* Input Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Scene ID or Share URL
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder="Enter 12-character scene ID or paste share URL"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handlePasteFromClipboard}
            className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            Paste
          </button>
        </div>
        {error && (
          <p className="text-red-600 text-sm mt-1">{error}</p>
        )}
      </div>

      {/* Preview Button */}
      {sceneId && !showPreview && !isLoading && (
        <div className="flex justify-end">
          <button
            onClick={handlePreview}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Preview Scene
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading scene preview...</p>
        </div>
      )}

      {/* Preview Results */}
      {showPreview && loadResult && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-800 font-medium">Scene Preview</span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
              <div>
                <span className="font-medium">Scene ID:</span> {loadResult.metadata.id}
              </div>
              <div>
                <span className="font-medium">Size:</span> {(loadResult.metadata.size_bytes / 1024).toFixed(1)}KB
              </div>
              <div>
                <span className="font-medium">Version:</span> {loadResult.metadata.schema_version}
              </div>
              <div>
                <span className="font-medium">Created:</span> {new Date(loadResult.metadata.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Warning about unsaved changes */}
          {hasUnsavedChanges && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-yellow-800 text-sm">
                  Loading this scene will replace your current work. Make sure to save any important changes first.
                </span>
              </div>
            </div>
          )}

          {/* Load Button */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowPreview(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLoadScene}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Load Scene
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 